import { Button, SkeletonTable, SkeletonText } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useCostCentersQuery } from 'modules/budgets/apis';
import { useExpenseCategoriesQuery } from 'modules/company/expense-categories/containers/EcContainer/hooks/useExpenseCategoriesQuery';
import { TaskStepLayout } from 'modules/onboarding/components/TaskStepLayout';
import { getHelpVideoUrl } from 'modules/onboarding/setup-hub/pages/ControlSettings/utils';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { routeFor } from 'src/core/constants/routes';
import { ExpenseCategoryTable } from 'src/core/modules/company/expense-categories/containers/EcContainer/ExpenseCategoryTable';
import { EcDeletionModalContainer } from 'src/core/modules/company/expense-categories/containers/EcDeletionModalContainer';
import {
  EcCreationFormModalContainer,
  EcEditFormModalContainer,
} from 'src/core/modules/company/expense-categories/containers/EcFormModalContainer';
import { type ExpenseCategory } from 'src/core/modules/company/expense-categories/expenseCategory';

import { routes } from '../../routes';

export const ControlSettingsExpenseCategoryList = () => {
  const { t, activeLanguage } = useTranslation('global');
  const history = useHistory();

  const companyId = useCompanyId();

  const expenseCategoriesQueryState = useExpenseCategoriesQuery();
  const expenseCategories = unwrapQuery(expenseCategoriesQueryState) || [];

  const costCentersQueryState = useCostCentersQuery();
  const costCenters = unwrapQuery(costCentersQueryState) || [];

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingExpenseCategory, setEditingExpenseCategory] =
    useState<ExpenseCategory | null>(null);
  const [deletingExpenseCategory, setDeletingExpenseCategory] =
    useState<ExpenseCategory | null>(null);

  const hasSuccessfullyLoaded =
    expenseCategoriesQueryState.status === 'success' ||
    costCentersQueryState.status === 'success';

  const goToHub = () => {
    const hubPath = routeFor(routes.SETUP_HUB_TASK_LIST.path, {
      company: companyId,
    });
    history.push(hubPath);
  };

  const goBack = () => {
    const hubPath = routeFor(
      routes.CONTROL_SETTINGS_NEW_EXPENSE_CATEGORY.path,
      {
        company: companyId,
      },
    );
    history.push(hubPath);
  };

  return (
    <TaskStepLayout
      onBack={goBack}
      onNext={goToHub}
      isLastStep
      description={t('setupHub.expenseCategoryList.callout.description')}
      video={{
        url: getHelpVideoUrl('expense_categories', activeLanguage),
        title: t('misc.needHelp'),
      }}
      title={t('misc.greatJob')}
    >
      <>
        {hasSuccessfullyLoaded ? (
          <>
            <ExpenseCategoryTable
              costCenters={costCenters}
              expenseCategories={expenseCategories}
              isCostCentersFeatureEnabled
              onDelete={setDeletingExpenseCategory}
              onEdit={setEditingExpenseCategory}
              rowHeight="normal"
              withBorder
            />
            <Button
              className="mx-auto mt-16 flex"
              onClick={() => setIsCreateModalOpen(true)}
              text={t('setupHub.expenseCategoryList.addCategoryCta')}
              variant="secondaryNeutral"
              iconPosition="left"
              iconName="plus"
            />
          </>
        ) : (
          <SkeletonTable
            numberOfRows={1}
            columns={[
              { cell: <SkeletonText />, header: <SkeletonText /> },
              { cell: <SkeletonText />, header: <SkeletonText /> },
            ]}
            withColumnSeparator
            withHeader
          />
        )}
        <EcCreationFormModalContainer
          isOpen={isCreateModalOpen}
          onComplete={() => setIsCreateModalOpen(false)}
          onCancel={() => setIsCreateModalOpen(false)}
          isMapSyncEnabled={false}
          isSplittable={false}
        />
        {editingExpenseCategory && (
          <EcEditFormModalContainer
            expenseCategory={editingExpenseCategory}
            isOpen={editingExpenseCategory !== null}
            isSplittable={false}
            onComplete={() => setEditingExpenseCategory(null)}
            onCancel={() => setEditingExpenseCategory(null)}
          />
        )}
        {deletingExpenseCategory && (
          <EcDeletionModalContainer
            expenseCategory={deletingExpenseCategory}
            isOpen={!!deletingExpenseCategory}
            onComplete={() => setDeletingExpenseCategory(null)}
            onClose={() => setDeletingExpenseCategory(null)}
            isMapSyncEnabled={false}
            fieldMapping={undefined}
            saveMappingChanges={() => {}}
          />
        )}
      </>
    </TaskStepLayout>
  );
};
